/**
 * Auto-sauvegarde du .geneatree après enrichissement de l'arbre en mémoire.
 *
 * Problème résolu : le .geneatree n'était généré qu'une fois, au moment de
 * la sauvegarde initiale — les événements extraits par l'IA (notes GEDCOM,
 * Wikipedia, documents) et les éditions manuelles vivaient uniquement en
 * mémoire et étaient perdus à la réouverture.
 *
 * Mécanisme : à chaque intégration/édition, régénère le cache compressé et
 * le pousse via PUT /files/:id/content (remplacement in-place, même fileId
 * → groupe, photos, partages conservés). Débouncé pour absorber les rafales
 * (intégration de N événements = un seul upload).
 *
 * Invités : l'endpoint est réservé au propriétaire — un 404 ("not owned")
 * désactive l'auto-save pour ce fichier sans bruit.
 */

import { storeEvents, EVENTS } from '../common/stores/storeEvents.js';
import configStore from '../tabs/fanChart/fanConfigStore.js';
import { logger } from '../utils/debug/logger.js';

const DEBOUNCE_MS = 8000;

class TreeAutoSaveService {
    constructor() {
        this._timer = null;
        this._saving = false;
        this._pendingReason = null;
        this._disabledForFile = null;
        this._dirty = false;
    }

    /** À appeler une fois au démarrage de l'application (bootstrap). */
    initialize() {
        storeEvents.subscribe(EVENTS.AI_NOTES.PROCESSING_COMPLETED, () => this.schedule('ai-notes'));
        storeEvents.subscribe(EVENTS.ENRICHMENT.EVENTS_INTEGRATED, ({ source } = {}) => this.schedule(source || 'enrichment'));
        storeEvents.subscribe(EVENTS.TREE.MODIFIED, ({ source } = {}) => this.schedule(source || 'edit'));

        // Course extraction/upload : si des enrichissements sont arrivés
        // AVANT que le fichier ne soit ancré dans files-service (sauvegarde
        // d'un import en cours d'analyse), les rejouer à l'ancrage.
        storeEvents.subscribe(EVENTS.FILE.CURRENT_CHANGED, ({ fileId } = {}) => {
            if (fileId && this._dirty) this.schedule('file-anchored');
        });
    }

    schedule(reason) {
        // Marquer l'arbre modifié même sans fileId : l'ancrage ultérieur
        // (FILE.CURRENT_CHANGED) rejouera la sauvegarde.
        this._dirty = true;

        const fileId = configStore.getConfig?.currentFileId;
        if (!fileId) return; // fichier non sauvegardé : on attend l'ancrage
        if (this._disabledForFile === fileId) return; // invité (404) — pas de retry

        clearTimeout(this._timer);
        this._timer = setTimeout(() => {
            this._timer = null;
            this._saveNow(reason);
        }, DEBOUNCE_MS);
    }

    async _saveNow(reason) {
        if (this._saving) {
            // Une sauvegarde est en cours : rejouer après
            this._pendingReason = reason;
            return;
        }

        const fileId = configStore.getConfig?.currentFileId;
        if (!fileId) return;

        this._saving = true;
        try {
            const { default: processedCacheService } = await import('../utils/processedCacheService.js');
            if (!processedCacheService.canGenerateCache()) return;

            const compressed = await processedCacheService.generateProcessedCache();
            if (!compressed || typeof compressed !== 'string') {
                throw new Error('cache compressé invalide');
            }

            const { filesService } = await import('./filesService.js');
            await filesService.updateFileContent(fileId, compressed, 'text/plain');

            this._dirty = false;
            logger.cache.info(`💾 Auto-save .geneatree OK (${reason}, ${(compressed.length / 1024).toFixed(0)} KB)`);
            const { default: notificationManager } = await import('../utils/NotificationManager.js');
            notificationManager.success('Arbre sauvegardé', 2000);
        } catch (error) {
            const msg = String(error?.message || error);
            if (/not owned|404/.test(msg)) {
                // Invité sur un fichier partagé : modification directe interdite,
                // ses enrichissements passent par le flux de propositions.
                this._disabledForFile = fileId;
                logger.cache.info('Auto-save désactivé pour ce fichier (lecteur non propriétaire)');
            } else {
                logger.cache.warn('Auto-save .geneatree échoué:', msg);
                const { default: notificationManager } = await import('../utils/NotificationManager.js');
                notificationManager.warning('Sauvegarde automatique échouée — vos enrichissements restent en mémoire', 4000);
            }
        } finally {
            this._saving = false;
            if (this._pendingReason) {
                const next = this._pendingReason;
                this._pendingReason = null;
                this.schedule(next);
            }
        }
    }
}

const treeAutoSaveService = new TreeAutoSaveService();
export default treeAutoSaveService;
